import React, { useState } from "react";
import { FaChevronDown, FaPlus, FaTrash } from "react-icons/fa";
import {
  TERMS_SECTION_ICON_OPTIONS,
  createEmptyTermsSection,
  getDefaultTermsCmsConfig,
} from "../../utils/termsModuleCms";
import RichTextEditor from "./RichTextEditor";
import "../../assets/styles/cms_contact_editor.scss";

function Field({ label, children, hint }) {
  return (
    <div className="cms-contact-editor__field">
      <label className="cms-contact-editor__label">{label}</label>
      {children}
      {hint ? <p className="cms-contact-editor__hint">{hint}</p> : null}
    </div>
  );
}

function VisibilityToggle({ checked, disabled, onChange, label }) {
  return (
    <div className="cms-visibility-toggle">
      <label className={`cms-on-off${checked ? " is-on" : " is-off"}`} title={label}>
        <input
          type="checkbox"
          className="cms-on-off__input"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={label}
        />
        <span className="cms-on-off__track" aria-hidden>
          <span className="cms-on-off__knob" />
        </span>
      </label>
      <span
        className={`cms-visibility-toggle__label${
          checked ? " cms-visibility-toggle__label--on" : " cms-visibility-toggle__label--off"
        }`}
      >
        {checked ? "Visible" : "Hidden"}
      </span>
    </div>
  );
}

export default function TermsCmsEditor({
  config,
  onChange,
  readOnly = false,
  moduleLabel = "Module",
  effectiveDate = "",
  onEffectiveDateChange,
}) {
  const data = config || getDefaultTermsCmsConfig("PLATFORM", moduleLabel);
  const sections = Array.isArray(data.sections) ? data.sections : [];
  const [expandedId, setExpandedId] = useState(sections[0]?.id || null);

  const patch = (partial) => onChange({ ...data, ...partial });

  const patchSection = (sectionId, partial) => {
    if (readOnly) return;
    patch({
      sections: sections.map((section) =>
        section.id === sectionId ? { ...section, ...partial } : section,
      ),
    });
  };

  const addSection = () => {
    if (readOnly) return;
    const next = createEmptyTermsSection(sections.length);
    patch({ sections: [...sections, next] });
    setExpandedId(next.id);
  };

  const removeSection = (sectionId) => {
    if (readOnly || sections.length <= 1) return;
    const next = sections.filter((section) => section.id !== sectionId);
    patch({ sections: next });
    if (expandedId === sectionId) setExpandedId(next[0]?.id || null);
  };

  const visibleCount = sections.filter((section) => section.visible !== false).length;

  return (
    <div className="cms-contact-editor">
      <p className="cms-contact-editor__intro">
        Configure the public <strong>Terms &amp; Conditions</strong> accordion for{" "}
        <strong>{moduleLabel}</strong>. Set section visibility, titles, summaries, and body content.
        Hero title and intro stay platform-controlled. Click <strong>Save</strong> to publish live.
      </p>

      {typeof onEffectiveDateChange === "function" ? (
        <section className="cms-contact-editor__section">
          <h3 className="cms-contact-editor__section-title">Publishing</h3>
          <Field
            label="Effective date"
            hint="Shown in the public page hero and version meta bar."
          >
            <input
              type="date"
              className="cms-contact-editor__input"
              value={effectiveDate || ""}
              disabled={readOnly}
              onChange={(e) => onEffectiveDateChange(e.target.value)}
            />
          </Field>
        </section>
      ) : null}

      <section className="cms-contact-editor__section">
        <div className="cms-contact-editor__emails-head">
          <h3 className="cms-contact-editor__section-title" style={{ margin: 0 }}>
            Policy sections
          </h3>
          <span className="cms-contact-editor__hint" style={{ margin: 0 }}>
            {visibleCount} of {sections.length} visible
          </span>
        </div>
        <p className="cms-contact-editor__hint" style={{ marginBottom: 12 }}>
          Expand a section to edit its title, card summary, icon, and main content. Hidden sections
          do not appear on the public page or in the On this page navigation.
        </p>

        <div className="cms-privacy-visibility-list">
          {sections.map((section, index) => {
            const isVisible = section.visible !== false;
            const isExpanded = expandedId === section.id;
            return (
              <div
                key={section.id}
                className={`cms-privacy-section-card${isVisible ? "" : " is-hidden"}${
                  isExpanded ? " is-expanded" : ""
                }`}
              >
                <div className="cms-privacy-section-card__head">
                  <button
                    type="button"
                    className="cms-privacy-section-card__expand"
                    aria-expanded={isExpanded}
                    onClick={() => setExpandedId(isExpanded ? null : section.id)}
                  >
                    <span className="cms-privacy-visibility-row__num">{index + 1}</span>
                    <span className="cms-privacy-section-card__title">{section.title}</span>
                    <FaChevronDown
                      className={`cms-privacy-section-card__chevron${isExpanded ? " is-open" : ""}`}
                      aria-hidden
                    />
                  </button>
                  <VisibilityToggle
                    checked={isVisible}
                    disabled={readOnly}
                    onChange={(checked) => patchSection(section.id, { visible: checked })}
                    label={`${section.title}: ${isVisible ? "Visible" : "Hidden"}`}
                  />
                </div>

                {isExpanded ? (
                  <div className="cms-privacy-section-card__body">
                    <Field label="Title">
                      <input
                        type="text"
                        className="cms-contact-editor__input"
                        value={section.title || ""}
                        disabled={readOnly}
                        onChange={(e) => patchSection(section.id, { title: e.target.value })}
                      />
                    </Field>
                    <Field
                      label="Card summary"
                      hint="Short preview shown on the closed accordion card."
                    >
                      <textarea
                        className="cms-contact-editor__textarea"
                        rows={3}
                        value={section.summary || ""}
                        disabled={readOnly}
                        onChange={(e) => patchSection(section.id, { summary: e.target.value })}
                      />
                    </Field>
                    <Field label="Icon">
                      <select
                        className="cms-contact-editor__input"
                        value={section.icon || "file"}
                        disabled={readOnly}
                        onChange={(e) => patchSection(section.id, { icon: e.target.value })}
                      >
                        {TERMS_SECTION_ICON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Main content" hint="Shown when the section is expanded.">
                      <RichTextEditor
                        value={section.body || ""}
                        readOnly={readOnly}
                        placeholder="Write the section content…"
                        onChange={(html) => {
                          if (readOnly) return;
                          patchSection(section.id, { body: html });
                        }}
                      />
                    </Field>
                    {!readOnly && sections.length > 1 ? (
                      <button
                        type="button"
                        className="cms-contact-editor__delete-btn"
                        onClick={() => removeSection(section.id)}
                      >
                        <FaTrash aria-hidden />
                        Remove section
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        {!readOnly ? (
          <button
            type="button"
            className="cms-contact-editor__add-btn"
            style={{ marginTop: 12 }}
            onClick={addSection}
          >
            <FaPlus aria-hidden />
            Add section
          </button>
        ) : null}
      </section>
    </div>
  );
}
